import React, { Fragment, useState, useEffect, useRef } from 'react';

import intl from 'react-intl-universal';
import api from 'utils/api';
import localData from 'utils/localData';
import PropTypes from 'prop-types';
import { Title, Message, Actions, StyledButton } from 'shared/modals/Styles';
import { Card, Stack } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { IssuePropTypes } from 'shared/propTypes/issueType';
import { AttachmentProptypes } from 'shared/propTypes/attachmentType';
import { USER_COMMENT } from 'shared/constants/comment';
import { DOLPHIN_API } from 'shared/constants/apis';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('DownloadModalContent');

const propTypes = {
  issue: PropTypes.shape(IssuePropTypes).isRequired,
  attachment: PropTypes.shape(AttachmentProptypes).isRequired,
  modalClose: PropTypes.func,
};

const DownloadModalContent = ({ issue, attachment, modalClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [GetObjectPresignedURL, setGetObjectPresignedURL] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const isDownloadingRef = useRef(false);

  // Obtaining presigned url
  useEffect(() => {
    const config = {
      params: {
        organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
        attachmentId: attachment.id,
      },
    };
    api
      .get(`/api/download/${issue.id}`, config)
      .then(async (data) => {
        setGetObjectPresignedURL(data.GetObjectPresignedURL);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error);
        setIsLoading(false);
        log.error('DownloadModalContent error', error);
        enqueueSnackbar(intl.get('kanban_modal_text_obtain_download_failed'), {
          variant: 'error',
        });
      });
  }, []);

  const handleDownloadButtonClick = async () => {
    if (!GetObjectPresignedURL) {
      enqueueSnackbar(
        intl.get('kanban_modal_text_download_not_ready', {
          filename: attachment.title,
        }),
        {
          variant: 'warning',
        }
      );
      return;
    }

    setIsDownloading(true);
    const organizationId = localData.get(USER_COMMENT.CURRENT_ORGID);
    const ifSent = issue.srcOrgId === organizationId;

    const a = document.createElement('a');
    a.href = GetObjectPresignedURL;
    a.download = attachment.title || 'download';
    a.rel = 'noopener';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    //! Adds to logs
    api.post(DOLPHIN_API.LOGS, {
      params: {
        organizationId: organizationId,
      },
      data: {
        type: 'download',
        contents: [issue.title],
        link: ifSent
          ? `/sent/issues/${issue.id}`
          : `/received/my-task/issues/${issue.id}`,
      },
    });
    setTimeout(() => {
      setIsDownloading(false);
      enqueueSnackbar(
        intl.get('kanban_modal_text_download_started_success', {
          filename: attachment.title,
        }),
        {
          variant: 'success',
        }
      );
      modalClose();
    }, 1000);
  };

  const handleCancelButtonClick = () => {
    modalClose();
  };

  useEffect(() => {
    isDownloadingRef.current = isDownloading;
  }, [isDownloading]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDownloadingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <Fragment>
      <Card sx={{ padding: 4, minWidth: 500 }}>
        <Stack spacing={2}>
          <Title>{attachment.title}</Title>
          <Message>{intl.get('kanban_modal_text_download_hint')}</Message>
          <Actions>
            <StyledButton
              variant="primary"
              disabled={isLoading || !!error || isDownloading}
              onClick={handleDownloadButtonClick}>
              {isLoading ? (
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginRight: '8px' }}
                />
              ) : isDownloading ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress
                    size={20}
                    color="inherit"
                    style={{ marginRight: '8px' }}
                  />
                  {intl.get('kanban_modal_button_downloading')}
                </div>
              ) : error ? (
                intl.get('kanban_modal_button_retry')
              ) : (
                intl.get('kanban_modal_button_download')
              )}
            </StyledButton>
            <StyledButton hollow onClick={handleCancelButtonClick}>
              {intl.get('kanban_modal_button_download_cancel')}
            </StyledButton>
          </Actions>
        </Stack>
      </Card>
    </Fragment>
  );
};

DownloadModalContent.propTypes = propTypes;

export default DownloadModalContent;
